import React, { useState, useEffect } from 'react';
import { getNotificationPreferences, updateNotificationPreferences } from './api';
import { isPushSupported, enablePushNotifications, disablePushNotifications } from './pushNotifications';
import { SSG_COMPETITION_NAME } from './ssgConstants';

const PLAYER_TOGGLES = [
  { key: 'gps_upload',         label: 'GPS & Training data' },
  { key: 'medical_updates',    label: 'Medical updates' },
  { key: 'wellness_reminders', label: 'Wellness reminders' },
  { key: 'appointments',       label: 'Appointments' },
  { key: 'ssg_points',         label: `${SSG_COMPETITION_NAME} updates` },
];
const STAFF_TOGGLES = [
  { key: 'gps_upload_staff', label: 'GPS upload alerts' },
];

function ToggleRow({ label, checked, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{label}</span>
      <button onClick={onChange} role="switch" aria-checked={checked}
        style={{ width: 42, height: 24, borderRadius: 99, border: 'none', cursor: 'pointer', position: 'relative', background: checked ? '#3b82f6' : 'var(--border-strong)', flexShrink: 0, padding: 0 }}>
        <span style={{ position: 'absolute', top: 2, left: checked ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
      </button>
    </div>
  );
}

export default function NotificationsPanel({ user }) {
  const isPlayer = user?.role === 'player';
  const toggles  = isPlayer ? PLAYER_TOGGLES : STAFF_TOGGLES;

  const [prefs, setPrefs]       = useState(null);
  const [permission, setPermission] = useState(() =>
    isPushSupported() && typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const [enabling, setEnabling]   = useState(false);
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    getNotificationPreferences(user.id).then(setPrefs).catch(() => {});
  }, [user?.id]);

  const handleToggle = async key => {
    const next = { ...prefs, [key]: !prefs?.[key] };
    setPrefs(next);
    try { await updateNotificationPreferences(user.id, { [key]: next[key] }); } catch { /* best effort */ }
  };

  const handleEnable = async () => {
    setEnabling(true);
    const res = await enablePushNotifications(user.id);
    setEnabling(false);
    if (res.ok) setPermission('granted');
    else if (res.reason === 'denied') setPermission('denied');
    else if (res.reason === 'ios-not-installed') alert('Install HeraLabX to your home screen first (Safari Share → Add to Home Screen), then try again.');
  };

  const handleDisableAll = async () => {
    setDisabling(true);
    await disablePushNotifications();
    setDisabling(false);
    setPermission(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
  };

  if (permission === 'unsupported') {
    return <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Push notifications aren't supported on this browser.</p>;
  }

  return (
    <div>
      {permission !== 'granted' ? (
        <button onClick={handleEnable} disabled={enabling}
          style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: enabling ? 'var(--border)' : '#3b82f6', color: enabling ? 'var(--text-dimmed)' : '#fff', fontSize: 14, fontWeight: 700, cursor: enabling ? 'default' : 'pointer', fontFamily: 'inherit' }}>
          {enabling ? 'Enabling…' : 'Enable notifications'}
        </button>
      ) : (
        <>
          <div>
            {toggles.map(t => (
              <ToggleRow key={t.key} label={t.label} checked={!!prefs?.[t.key]} onChange={() => handleToggle(t.key)} />
            ))}
          </div>
          <button onClick={handleDisableAll} disabled={disabling}
            style={{ marginTop: 14, width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'transparent', color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: disabling ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            {disabling ? 'Disabling…' : 'Disable all notifications'}
          </button>
        </>
      )}
    </div>
  );
}
